import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Highlight, MaskedLine } from "@/components/BrandKit";
import { DoodleStar, DoodleSparkle } from "@/components/Doodles";

const HERO_BEAR = "/brand/bear-mascot.png";

const STRIP = [
  "World Building",
  "360° Campaigns",
  "Multi-Lingual",
  "Scale Ready",
  "Experimental",
  "AI-Native",
  "Taste First",
];

export default function Hero() {
  const [time, setTime] = useState("");

  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setTime(
        d.toLocaleTimeString("en-IN", {
          timeZone: "Asia/Kolkata",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      );
    };
    tick();
    const i = setInterval(tick, 30000);
    return () => clearInterval(i);
  }, []);

  const { scrollY } = useScroll();
  const bearY = useTransform(scrollY, [0, 600], [0, -60]);
  const bearRotate = useTransform(scrollY, [0, 800], [0, -3]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative border-b border-black bbs-noise overflow-hidden min-h-screen flex flex-col"
    >
      {/* Eyebrow row */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="max-w-[1480px] w-full mx-auto px-5 sm:px-8 pt-24"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#48B02C] animate-pulse" />
            <span>AI-First Creative Studio</span>
            <span className="hidden sm:inline text-[#5C5C5C]">
              · Bengaluru / Mumbai
            </span>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C5C5C]">
            {time} IST
          </div>
        </div>
      </motion.div>

      {/* Main content */}
      <div className="flex-1 max-w-[1480px] w-full mx-auto px-5 sm:px-8 pt-10 pb-10 flex items-center">
        <div className="grid grid-cols-12 gap-6 lg:gap-12 items-center w-full">
          {/* Left */}
          <div className="col-span-12 lg:col-span-7 order-2 lg:order-1">
            <h1
              data-testid="hero-wordmark"
              className="font-display uppercase leading-[0.9] tracking-[-0.035em] text-5xl sm:text-6xl md:text-7xl lg:text-[92px] xl:text-[104px]"
              style={{ fontWeight: 900 }}
            >
              <MaskedLine delay={0.2}>Big Bear</MaskedLine>
              <MaskedLine delay={0.35}>
                Studios<span className="text-[#FF4D00]">.</span>
              </MaskedLine>
            </h1>

            <div
              data-testid="hero-tagline"
              className="mt-8 font-display text-2xl sm:text-3xl lg:text-[42px] leading-[1.1] tracking-tight max-w-2xl"
              style={{ fontWeight: 800 }}
            >
              <MaskedLine delay={0.65}>
                <span>We&apos;re serious about </span>
              </MaskedLine>
              <MaskedLine delay={0.8}>
                <span>making </span>
                <Highlight color="#FF4D00" rotate={-2}>
                  fun
                </Highlight>
                <span> films.</span>
              </MaskedLine>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.7 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <button
                data-testid="hero-cta-work"
                onClick={() => scrollTo("contact")}
                className="group inline-flex items-center gap-2 bg-black text-[#fdfbf7] px-7 h-13 py-4 font-grotesk uppercase tracking-[0.16em] text-[12px] border border-black hover:bg-[#FF4D00] transition-colors"
              >
                Work with us
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-12" />
              </button>
              <button
                data-testid="hero-cta-see-work"
                onClick={() => scrollTo("work")}
                className="group inline-flex items-center gap-2 bg-transparent text-black px-7 py-4 font-grotesk uppercase tracking-[0.16em] text-[12px] border border-black hover:bg-black hover:text-[#fdfbf7] transition-colors"
              >
                See the work
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-12" />
              </button>
            </motion.div>
          </div>

          {/* Right - bear logo on painted color-block canvas */}
          <div className="col-span-12 lg:col-span-5 order-1 lg:order-2">
            <motion.div
              style={{ y: bearY, rotate: bearRotate }}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="relative border border-black overflow-hidden aspect-[4/5] sm:aspect-[5/6] lg:aspect-[4/5]">
                {/* Painted color-block backdrop - shades of violet / purple */}
                <div className="absolute inset-0 grid grid-cols-6 grid-rows-6">
                  <div className="col-span-4 row-span-3 bg-[#F1E9FA]" />
                  <div className="col-span-2 row-span-3 bg-[#B79BE0]" />
                  <div className="col-span-2 row-span-3 bg-[#7752B8]" />
                  <div className="col-span-2 row-span-3 bg-[#4A2F87]" />
                  <div className="col-span-2 row-span-3 bg-[#341F65]" />
                </div>
                {/* Bear mascot - upright, bleeding off the bottom edge */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <img
                    src={HERO_BEAR}
                    alt="Big Bear Studios mascot"
                    className="w-full h-full object-contain drop-shadow-[0_16px_28px_rgba(52,31,101,0.35)]"
                    style={{
                      transform: "scale(1.18) translateY(13%)",
                    }}
                  />
                </div>
                {/* subtle spotlight */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 40%, transparent 45%, rgba(0,0,0,0.14) 100%)",
                  }}
                />
              </div>
              {/* floating doodles */}
              <motion.div
                animate={{ y: [0, -10, 0], rotate: [0, 8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-4 -left-4 text-[#B79BE0]"
              >
                <DoodleStar className="w-10 h-10" />
              </motion.div>
              <motion.div
                animate={{ y: [0, 8, 0], rotate: [0, -6, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                className="absolute -bottom-3 -right-3 text-[#4A2F87]"
              >
                <DoodleSparkle className="w-8 h-8" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom marquee */}
      <div className="border-t border-black bg-black text-[#fdfbf7] overflow-hidden">
        <div className="bbs-marquee-wrap">
          <div className="bbs-marquee-track py-4 sm:py-5" data-testid="hero-strip">
            {Array.from({ length: 4 })
              .flatMap(() => STRIP)
              .map((s, i) => (
                <span
                  key={i}
                  className="font-display uppercase text-xl sm:text-2xl tracking-tight px-8 inline-flex items-center"
                  style={{ fontWeight: 800 }}
                >
                  <span>{s}</span>
                  <span className="text-[#FF4D00] mx-8">✦</span>
                </span>
              ))}
          </div>
        </div>
      </div>
    </section>
  );
}
